/**
 * Human-readable RockBand signal labels for the unified crosshair tooltip
 * (R119). Pure / stateless — used by the chart tooltip (and re-usable by the
 * alerts UI). EXIT_LONG = SELL, EXIT_SHORT = COVER.
 */
import type { RockBandStrength } from './rockBand';

export interface CondLabelInputs {
  signal: 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT';
  strength: RockBandStrength;       // 'HIGH' | 'MEDIUM' | 'LOW' | null
}

export interface CondLabel {
  /** Short heading e.g. "BUY · High". Always defined. */
  heading: string;
  /** One-line plain-English description of why the signal fired. */
  detail: string;
  /** 'bull' / 'bear' / 'flat' — drives colour for the row. */
  tone: 'bull' | 'bear' | 'flat';
}

const STRENGTH_WORD: Record<'HIGH' | 'MEDIUM' | 'LOW', string> = {
  HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low',
};

const BUY_DETAIL: Record<'HIGH' | 'MEDIUM' | 'LOW', string> = {
  HIGH:   'Strong-buy candle with a green (Strong Buy) HTF background.',
  MEDIUM: 'Strong-buy candle; HTF background turned Blue (Probable Buy) from below — trending up.',
  LOW:    'Strong-buy candle; HTF background turned Black (Neutral) up from a short zone.',
};
const SHORT_DETAIL: Record<'HIGH' | 'MEDIUM' | 'LOW', string> = {
  HIGH:   'Strong-short candle with a red (Strong Short) HTF background.',
  MEDIUM: 'Strong-short candle; HTF background turned Orange (Probable Short) from above — trending down.',
  LOW:    'Strong-short candle; HTF background turned Black (Neutral) down from a buy zone.',
};

export function rockBandCondLabel(inp: CondLabelInputs): CondLabel {
  const { signal, strength } = inp;
  if (signal === 'EXIT_LONG')  return { heading: 'SELL',  detail: 'Candle turned Strong Short — exit long.', tone: 'flat' };
  if (signal === 'EXIT_SHORT') return { heading: 'COVER', detail: 'Candle turned Strong Buy — exit short.', tone: 'flat' };
  const tone: 'bull' | 'bear' = signal === 'BUY' ? 'bull' : 'bear';
  const side = signal === 'BUY' ? 'BUY' : 'SHORT';
  const sKey = (strength === 'HIGH' || strength === 'MEDIUM' || strength === 'LOW') ? strength : null;
  const heading = sKey ? `${side} · ${STRENGTH_WORD[sKey]}` : side;
  const detail = sKey
    ? (signal === 'BUY' ? BUY_DETAIL[sKey] : SHORT_DETAIL[sKey])
    : 'RockBand entry signal.';
  return { heading, detail, tone };
}
